import posthog, { SurveyEventName, SurveyEventProperties } from 'posthog-js';
import type { Properties } from 'posthog-js';
import { config } from 'env';

import type { AnalyticsClient, AnalyticsPersonProps, AnalyticsProperties, FeatureFlagValue, SurveySubmission } from './types';
import type { AnalyticsEventName } from './events';

// The `survey sent` payload PostHog documents for surveys drawn by your own UI.
export function surveySentProperties({ surveyId, submissionId, questionIds, responses }: SurveySubmission): Properties {
  // Read at call time, not import time: importing this module must never throw.
  const { SURVEY_ID, SURVEY_RESPONSE, SURVEY_SUBMISSION_ID, SURVEY_COMPLETED } = SurveyEventProperties;
  const properties: Properties = { [SURVEY_ID]: surveyId, [SURVEY_SUBMISSION_ID]: submissionId, [SURVEY_COMPLETED]: true };
  responses.forEach((response, index) => {
    const questionId = questionIds[index];
    if (response !== null && questionId) properties[`${SURVEY_RESPONSE}_${questionId}`] = response;
  });
  return properties;
}

// ==============================|| ANALYTICS — POSTHOG CLIENT (LAYER 1) ||============================== //
// The ONLY module that imports posthog-js. Initialised synchronously at boot
// (index.tsx) so feature flags are requested before first paint — the
// welcome-tour experiment resolves against a 1.5s timeout and every ms of
// delay pushes real users into the control arm.
//
// Every SDK call is wrapped: analytics must never break the app.

// Reproduces the SDK behaviour that shipped with get-started (PostHog's
// dated preset: pageviews, autocapture, heatmaps, web vitals on). Turning any
// of these off is a product/privacy decision to be made explicitly — plan
// §18.5 — not inherited from whichever branch merged last.
const POSTHOG_DEFAULTS = '2026-05-30';

class PostHogAnalyticsClient implements AnalyticsClient {
  private initialized = false;

  // Arrow fields keep `this` bound when consumers destructure these.
  init = (): void => {
    if (this.initialized) return;
    try {
      // Our surveys are drawn by our own UI, so the SDK never needs their definitions; asking for them
      // hits /api/surveys/, which Cloudflare Access gates, and floods the console with CORS errors.
      posthog.init(config.posthog_key, { api_host: config.posthog_host, defaults: POSTHOG_DEFAULTS, disable_surveys: true });
      this.initialized = true;
    } catch {
      /* analytics never throws — stays uninitialised and every call no-ops */
    }
  };

  isReady = (): boolean => this.initialized;

  capture = (event: AnalyticsEventName, properties?: AnalyticsProperties): void => {
    this.run(() => posthog.capture(event, properties));
  };

  identify = (distinctId: string, properties?: AnalyticsPersonProps): void => {
    this.run(() => posthog.identify(distinctId, properties));
  };

  setPersonProperties = (properties: AnalyticsPersonProps): void => {
    this.run(() => posthog.setPersonProperties(properties));
  };

  reset = (): void => {
    this.run(() => posthog.reset());
  };

  onFeatureFlags = (cb: () => void): void => {
    this.run(() => posthog.onFeatureFlags(cb));
  };

  reloadFeatureFlags = (): void => {
    this.run(() => posthog.reloadFeatureFlags());
  };

  getFeatureFlag = (key: string, sendEvent: boolean = true): FeatureFlagValue => {
    if (!this.initialized) return undefined;
    try {
      return posthog.getFeatureFlag(key, { send_event: sendEvent });
    } catch {
      return undefined;
    }
  };

  surveyShown = (surveyId: string): void => this.surveyLifecycle(SurveyEventName.SHOWN, surveyId);

  surveyDismissed = (surveyId: string): void => this.surveyLifecycle(SurveyEventName.DISMISSED, surveyId);

  surveySent = (submission: SurveySubmission): void => {
    this.run(() => posthog.capture(SurveyEventName.SENT, surveySentProperties(submission)));
  };

  // `survey shown` and `survey dismissed` carry only the survey they belong to.
  private surveyLifecycle(event: string, surveyId: string): void {
    this.run(() => posthog.capture(event, { [SurveyEventProperties.SURVEY_ID]: surveyId }));
  }

  // A throwing SDK must never reach a component; before init, calls are dropped.
  private run(call: () => void): void {
    if (!this.initialized) return;
    try {
      call();
    } catch {
      /* analytics never throws */
    }
  }
}

export const posthogClient: AnalyticsClient = new PostHogAnalyticsClient();
